import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCampaignCreateControllerCampaignCreateMutation,
  useCampaignListControllerCampaignListQuery,
} from '@shared/services/apiService/apiService';
import { ResponseMsg } from '@shared/types/admin/agent';
import { CreateCampaignFormValue } from '../model/type';
import { generatePath, useNavigate } from 'react-router-dom';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import { createAppTheme } from '@shared/theme/Theme';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useCreateCampaign = () => {
  const [createCampaignMutation, { isLoading }] =
    useCampaignCreateControllerCampaignCreateMutation();

  const [responseMsg, setResponseMsg] = useState<ResponseMsg>({
    message: '',
    type: 'success',
  });
  const [responseLoadingFlag, setResponseLoadingFlag] = useState(false);

  const { refetch } = useCampaignListControllerCampaignListQuery();

  const { handleSubmit, setValue, reset, ...formMethods } = useForm<CreateCampaignFormValue>({
    mode: 'all',
    defaultValues: {
      title: '',
    },
  });

  const navigate = useNavigate();

  const runRedirectBlurTransition = async (id: string) => {
    if (typeof document === 'undefined') return;

    const storedMode = (() => {
      try {
        const raw = localStorage.getItem('customizer');
        const mode = raw ? (JSON.parse(raw) as { activeMode?: string }).activeMode : undefined;
        return mode === 'dark' ? 'dark' : 'light';
      } catch {
        return 'light';
      }
    })();
    const theme = createAppTheme({ mode: storedMode });
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '2000';
    overlay.style.pointerEvents = 'none';
    overlay.style.background = theme.palette.surface.overlay;
    overlay.style.backdropFilter = 'blur(0px)';
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 260ms ease, backdrop-filter 260ms ease';

    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      overlay.style.backdropFilter = 'blur(6px)';
    });

    await wait(220);
    navigate(generatePath(AuthenticatedPath.EDIT_CAMPAIGN_V2, { campaignId: id }));

    setTimeout(() => {
      overlay.style.opacity = '0';
      overlay.style.backdropFilter = 'blur(0px)';
      setTimeout(() => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 220);
    }, 120);
  };

  const onSuccessfullyCreatedCampaign = async (id: string) => {
    await runRedirectBlurTransition(id);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      setResponseLoadingFlag(true);
      const payload = await createCampaignMutation({
        campaignCreateRequestDto: {
          title: data.title,
          type: data.type,
          reservedLimit: data.reservedLimit,
        },
      }).unwrap();
      await refetch();
      await onSuccessfullyCreatedCampaign(payload.id);
      setResponseMsg({
        message: 'Successfully created',
        type: 'success',
      });
    } catch (error) {
      setResponseLoadingFlag(false);
      setResponseMsg({
        message: 'Something Went wrong',
        type: 'error',
      });
      throw error;
    }
  });

  return {
    setValue,
    reset,
    onSubmit,
    isLoading,
    responseMsg,
    setResponseMsg,
    responseLoadingFlag,
    ...formMethods,
  };
};
